import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, MessageSquare, MousePointer2, BrainCircuit, Sparkles } from 'lucide-react';
import { SectionHeading } from './ui-kit/SectionHeading';
import { Reveal } from './ui-kit/Reveal';
import { BrandButton } from './ui-kit/BrandButton';

const STEP_ICONS = [Sparkles, MousePointer2, BrainCircuit, MessageSquare];

/**
 * Brand-agent product explanation: 4 progressive steps that explain how
 * Agencive's main product (the brändiagentti) is built — from social-media
 * character to converting on-site assistant.
 */
const BrandAgentExplanation = () => {
  const { t } = useLanguage();

  return (
    <section
      id="brandiagentti"
      data-testid="brand-agent-section"
      className="bg-bg py-24 md:py-28 scroll-mt-24"
    >
      <div className="container-page">
        <SectionHeading
          eyebrow={t.brandAgent.eyebrow}
          title={t.brandAgent.title}
          lead={t.brandAgent.subtitle}
        />

        {/* Connected step rail (desktop) */}
        <div className="mt-14 relative">
          <div
            aria-hidden
            className="hidden lg:block absolute top-11 left-[12%] right-[12%] h-px bg-line"
          />
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 relative">
            {t.brandAgent.steps.map((step, i) => {
              const Icon = STEP_ICONS[i % STEP_ICONS.length];
              return (
                <Reveal as="li" key={step.title} delay={i * 70}>
                  <article
                    data-testid={`brand-agent-step-${i + 1}`}
                    className="relative h-full bg-surface border border-line rounded-card p-6 md:p-7 transition-[transform,border-color] duration-200 ease-out-quart hover:-translate-y-[3px] hover:border-ink/30"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink text-white text-[12px] font-semibold">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-input bg-brand-soft text-brand">
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      </span>
                    </div>
                    <h3 className="mt-5 text-[19px] md:text-[20px] font-semibold text-ink tracking-tight">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] text-ink-muted leading-relaxed">
                      {step.text}
                    </p>
                  </article>
                </Reveal>
              );
            })}
          </ol>
        </div>

        {/* Soft inline CTA */}
        <Reveal delay={200}>
          <div className="mt-12 md:mt-16 rounded-card bg-brand-soft border border-brand/20 p-7 md:p-9 grid md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <h3 className="text-h3 text-ink">{t.brandAgent.ctaTitle}</h3>
              <p className="mt-2 text-body text-ink-muted max-w-xl">{t.brandAgent.ctaSubtitle}</p>
            </div>
            <div className="md:col-span-4 md:flex md:justify-end">
              <BrandButton
                to="/yhteys#contact-form"
                variant="primary"
                iconRight={ArrowRight}
                data-testid="brand-agent-cta-btn"
              >
                {t.brandAgent.cta}
              </BrandButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default BrandAgentExplanation;
